<script lang="ts">
  import type { Snippet } from 'svelte';
  import { Link } from 'svelte-routing';
  import Spinner from './Spinner.svelte';

  // Single button primitive for every clickable in the app. Renders as
  // <button> by default; if `href` is given, renders as svelte-routing
  // <Link> for internal nav (preserves SPA routing) or <a> for external.
  // External is detected by `://` in the URL — anything else is treated
  // as a relative SPA path.
  //
  // `loading` swaps the visible label for a Spinner but keeps the button
  // width steady (label stays in the DOM, hidden) so clicking doesn't
  // cause the surrounding layout to jitter.

  type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
  type Size = 'sm' | 'md' | 'lg';

  interface Props {
    variant?: Variant;
    size?: Size;
    type?: 'button' | 'submit' | 'reset';
    loading?: boolean;
    disabled?: boolean;
    /** Internal SPA path (no protocol) → <Link>. External URL → <a target=_blank>. */
    href?: string;
    onclick?: (event: MouseEvent) => void;
    /** Stretch to fill the parent's inline axis. */
    block?: boolean;
    children: Snippet;
  }

  let {
    variant = 'primary',
    size = 'md',
    type = 'button',
    loading = false,
    disabled = false,
    href,
    onclick,
    block = false,
    children,
  }: Props = $props();

  const isExternal = $derived(typeof href === 'string' && href.includes('://'));
  const isDisabled = $derived(disabled || loading);

  const klass = $derived(
    [
      'ui-button',
      `ui-button--${variant}`,
      `ui-button--${size}`,
      block ? 'ui-button--block' : '',
      loading ? 'ui-button--loading' : '',
    ]
      .filter(Boolean)
      .join(' '),
  );
</script>

{#if href && !isDisabled}
  {#if isExternal}
    <a class={klass} {href} target="_blank" rel="noopener noreferrer">
      {#if loading}<span class="ui-button__spinner"><Spinner {size} /></span>{/if}
      <span class="ui-button__label">{@render children()}</span>
    </a>
  {:else}
    <Link to={href} getProps={() => ({ class: klass })}>
      {#if loading}<span class="ui-button__spinner"><Spinner {size} /></span>{/if}
      <span class="ui-button__label">{@render children()}</span>
    </Link>
  {/if}
{:else}
  <button class={klass} {type} disabled={isDisabled} {onclick}>
    {#if loading}<span class="ui-button__spinner"><Spinner {size} /></span>{/if}
    <span class="ui-button__label">{@render children()}</span>
  </button>
{/if}
